/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import layoutConf from "../../config/layoutConf";
import CreateRecordButton from "../input/CreateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import { cloneDeep } from "lodash";
/**
 * @COMPONENT_NAME MaterialPurposeUnit
 * @DESCRIPTION 품목-용도별 단위를 등록하고 관리하는 화면
 *             화면 상단에 품목 목록을 표시하고 하단에 해당 품목 용도-단위에 대한 관리 화면을 제공
 * @STATE
 *    - {Object} schema - 테이블 스키마 정보
 *    - {Object} grid - 그리드 객체 참조
 *    - {Object} api - API 객체 참조
 *    - {Object} topGrid - 상단 그리드 객체 참조
 *    - {Object} topSelect - 상단 그리드에서 선택된 데이터
 *    - {Object} grid - 하단 그리드 객체 참조
 *    - {Object} select - 하단 그리드에서 선택된 데이터
 *    - {number} height - 컴포넌트 높이 값
 *
 * @HANDLERS
 *    - {Function} handleResize - 화면 크기 변경 시 컴포넌트 높이를 조정하는 함수
 *    - {Function} onApiInitialized - API 초기화 완료 시 호출되는 함수
 *    - {Function} onInitialized - 그리드 초기화 완료 시 호출되는 함수
 *
 * @CALLBACKS
 *    - {Function} onSuccess - 작업 성공 시 알림 콜백
 *    - {Function} onFailure - 작업 실패 시 오류 알람 콜백
 *    - {Function} onNotifyWarning - 경고 알림 콜백
 *    - {Function} notifySuccess - 성공 알림 콜백
 *    - {Function} notifyFailure - 실패 알림 콜백
 *    - {Function} notifyWarning - 경고 알림 콜백
 */

const MaterialPurposeUnit = () => {
    const table = "MapMaterialPurposeUnit";
    const view = "VwMapMaterialPurposeUnit";

    const [height, setHeight] = useState(0);

    const [schema, setSchema] = useState();
    const [modifiedSchema, setModifiedSchema] = useState();
    const [api, setApi] = useState();
    const [topGrid, setTopGrid] = useState();
    const [topSelect, setTopSelect] = useState();

    const [grid, setGrid] = useState();
    const [select, setSelect] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }
        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid) => {
                setSelect(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            grid.selectionChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });

            grid.itemsSourceChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });
        }
    }, [grid]);

    useEffect(() => {
        if (topGrid) {
            const handleGridSelection = (grid) => {
                setTopSelect(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            topGrid.selectionChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });

            topGrid.itemsSourceChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });
        }
    }, [topGrid]);

    useEffect(() => {
        if (schema && topSelect) {
            const newSchema = cloneDeep(schema);
            newSchema.columns.forEach((column) => {
                if (column.name === "unitId") {
                    column.codeInfo.tableName = `MaterialUnitGroupDetail/${topSelect?.materialCode}/${topSelect?.materialCodeVer}/unitList`;
                }
            });
            setModifiedSchema(newSchema);
        }
        setApi((api) => {
            if (api) {
                api.refresh();
            }
            return api;
        });
    }, [schema, topSelect]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid, position) => {
        if (position === "top") {
            setTopGrid(grid);
        } else {
            setGrid(grid);
        }
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.5}>
                    <BasicCrudScreenType
                        headerTitle={t("term.material")}
                        id="MomSysMstMaterial"
                        table="MomSysMstMaterial"
                        view="MomSysZvwMaterial"
                        onInitialized={(grid) => onInitialized(grid, "top")}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        embededInOtherScreenType
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement flex={0.5}>
                    <BasicCrudScreenType
                        headerTitle={t("term.materialPurposeUnit")}
                        id={table}
                        table={table}
                        view={view}
                        onModifyViewUrl={(url) => `${url}/${topSelect?.materialCode}/${topSelect?.materialCodeVer}`}
                        onInitialized={onInitialized}
                        onApiInitialized={onApiInitialized}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                        <CreateRecordButton
                                            id={table}
                                            schema={modifiedSchema}
                                            refresh={api}
                                            setInitialDataCallback={(formData) => {
                                                formData["materialCode"] = topSelect?.materialCode;
                                                formData["materialCodeVer"] = topSelect?.materialCodeVer;
                                            }}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="PATCH">
                                        <UpdateRecordButton
                                            id={table}
                                            schema={modifiedSchema}
                                            grid={grid}
                                            refresh={api}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            disabled={select?.length === 0}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                        <DeleteRecordButton
                                            id={table}
                                            schema={schema}
                                            refresh={api}
                                            grid={grid}
                                            color="secondary"
                                            disabled={select?.length === 0}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onNotifyWarning}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                            </>
                        )}
                    />
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};

export default MaterialPurposeUnit;
